import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import apiClient from '../../api/client';

// ─── Types ──────────────────────────────────────────────────────────────

export type ExportDataType = 'molecules' | 'reactions' | 'proteins' | 'table';

interface FormatOption {
  id: string;
  label: string;
  ext: string;
}

const FORMAT_OPTIONS: Record<ExportDataType, FormatOption[]> = {
  molecules: [
    { id: 'sdf', label: 'SDF (Structure-Data)', ext: '.sdf' },
    { id: 'mol', label: 'MOL (Single Molecule)', ext: '.mol' },
    { id: 'csv', label: 'CSV', ext: '.csv' },
    { id: 'xlsx', label: 'Excel', ext: '.xlsx' },
    { id: 'json', label: 'JSON', ext: '.json' },
    { id: 'smi', label: 'SMILES', ext: '.smi' },
    { id: 'dwar', label: 'DataWarrior', ext: '.dwar' },
    { id: 'pdf', label: 'PDF Report', ext: '.pdf' },
  ],
  reactions: [
    { id: 'csv', label: 'CSV', ext: '.csv' },
    { id: 'rdf', label: 'RDF (Multi-Reaction)', ext: '.rdf' },
    { id: 'rxn', label: 'RXN (Single Reaction)', ext: '.rxn' },
  ],
  proteins: [
    { id: 'fasta', label: 'FASTA', ext: '.fasta' },
    { id: 'csv', label: 'CSV', ext: '.csv' },
    { id: 'pdb', label: 'PDB', ext: '.pdb' },
  ],
  table: [
    { id: 'csv', label: 'CSV', ext: '.csv' },
    { id: 'xlsx', label: 'Excel', ext: '.xlsx' },
  ],
};

interface ExportButtonProps {
  /** What kind of data we're exporting */
  dataType: ExportDataType;
  /** Total number of items available */
  totalCount: number;
  /** Number of selected items (0 = none selected, will export all) */
  selectedCount?: number;

  // ── Molecule export props ─────────────────────────────────────
  fileId?: number;
  moleculeIds?: number[];

  // ── Reaction export props ─────────────────────────────────────
  reactionIds?: number[];
  projectId?: number;

  // ── Protein export props ──────────────────────────────────────
  proteinIds?: number[];

  // ── Table export props ────────────────────────────────────────
  tableData?: Record<string, any>[];
  tableColumns?: string[];

  /** Custom button className override */
  className?: string;
  /** Compact mode (icon only, no label) */
  compact?: boolean;
  /** Increment to programmatically open the export menu from external code */
  openTrigger?: number;
}

// ─── Component ──────────────────────────────────────────────────────────

const ExportButton: React.FC<ExportButtonProps> = ({
  dataType,
  totalCount,
  selectedCount = 0,
  fileId,
  moleculeIds,
  reactionIds,
  projectId,
  proteinIds,
  tableData,
  tableColumns,
  className,
  compact = false,
  openTrigger = 0,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [exportingFormat, setExportingFormat] = useState<string | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0 });

  // Open menu when triggered externally (e.g. from SelectionActionBar)
  useEffect(() => {
    if (openTrigger > 0) setIsOpen(true);
  }, [openTrigger]);

  const formats = FORMAT_OPTIONS[dataType];
  const hasSelection = selectedCount > 0;
  const exportCount = hasSelection ? selectedCount : totalCount;
  const scopeLabel = hasSelection ? `Selected (${exportCount.toLocaleString()})` : `All (${exportCount.toLocaleString()})`;

  // ── Position menu ─────────────────────────────────────────────
  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const menuWidth = 220;
      let left = rect.right - menuWidth;
      if (left < 8) left = rect.left;
      if (left + menuWidth > window.innerWidth - 8) left = window.innerWidth - menuWidth - 8;
      setMenuPos({ top: rect.bottom + 4, left });
    }
  }, [isOpen]);

  // ── Click outside ─────────────────────────────────────────────
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: MouseEvent) => {
      if (
        menuRef.current && !menuRef.current.contains(e.target as Node) &&
        buttonRef.current && !buttonRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isOpen]);

  // ── Export handler ────────────────────────────────────────────
  const handleExport = useCallback(async (formatId: string) => {
    setIsExporting(true);
    setExportingFormat(formatId);
    setIsOpen(false);

    try {
      let endpoint = '';
      let body: any = {};

      if (dataType === 'molecules') {
        endpoint = '/export/molecules';
        body = { file_id: fileId, molecule_ids: hasSelection ? (moleculeIds || []) : [], format: formatId };
      } else if (dataType === 'reactions') {
        endpoint = '/export/reactions';
        body = { reaction_ids: hasSelection ? (reactionIds || []) : [], project_id: projectId, format: formatId };
      } else if (dataType === 'proteins') {
        endpoint = '/export/proteins';
        body = { protein_ids: hasSelection ? (proteinIds || []) : [], file_id: fileId, format: formatId };
      } else if (dataType === 'table') {
        endpoint = '/export/table';
        body = { data: tableData || [], columns: tableColumns || [], format: formatId };
      }

      const response = await apiClient.post(endpoint, body, { responseType: 'blob' });

      // Extract filename from Content-Disposition header
      const disposition = response.headers['content-disposition'] || '';
      const filenameMatch = disposition.match(/filename="?([^";\n]+)"?/);
      const filename = filenameMatch ? filenameMatch[1] : `export.${formatId}`;

      // Trigger download
      const blob = new Blob([response.data]);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err: any) {
      console.error('Export failed:', err);
    } finally {
      setIsExporting(false);
      setExportingFormat(null);
    }
  }, [dataType, fileId, moleculeIds, reactionIds, projectId, proteinIds, tableData, tableColumns, hasSelection]);

  // ── Render ────────────────────────────────────────────────────
  const defaultClassName = "px-4 py-2 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 text-sm font-medium rounded-xl border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 hover:-translate-y-0.5 hover:shadow-lg transition-all duration-200 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed";

  return (
    <>
      <button
        ref={buttonRef}
        onClick={() => setIsOpen(!isOpen)}
        disabled={isExporting || totalCount === 0}
        className={className || defaultClassName}
        title={totalCount === 0 ? 'No data to export' : `Export ${scopeLabel}`}
      >
        {isExporting ? (
          <div className="w-4 h-4 border-2 border-gray-300 dark:border-gray-600 border-t-gray-600 dark:border-t-gray-300 rounded-full animate-spin" />
        ) : (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
          </svg>
        )}
        {!compact && (
          <>
            <span>{isExporting ? 'Exporting...' : 'Export'}</span>
            <svg className={`w-3.5 h-3.5 transition-transform ${isOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </>
        )}
      </button>

      {isOpen && createPortal(
        <div
          ref={menuRef}
          style={{ position: 'fixed', top: menuPos.top, left: menuPos.left, zIndex: 9999 }}
          className="w-56 bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 py-1 animate-in fade-in slide-in-from-top-1"
        >
          {/* Scope label */}
          <div className="px-3 py-2 border-b border-gray-100 dark:border-gray-700">
            <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
              Export {scopeLabel}
            </span>
          </div>

          {/* Format options */}
          {formats.map((fmt) => (
            <button
              key={fmt.id}
              onClick={() => handleExport(fmt.id)}
              disabled={isExporting}
              className="w-full px-3 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors flex items-center justify-between group disabled:opacity-50"
            >
              <span>{fmt.label}</span>
              <span className="text-xs text-gray-400 dark:text-gray-500 group-hover:text-gray-600 dark:group-hover:text-gray-400">{fmt.ext}</span>
            </button>
          ))}

        </div>,
        document.body,
      )}
    </>
  );
};

export default ExportButton;
